import { useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveTemplate,
  archiveTemplate,
  getTemplateLifecycle,
  listTemplateVersions,
  publishTemplate,
  rejectTemplate,
  restoreTemplateVersion,
  submitTemplate,
  templatesKeys,
  RejectTemplateBodySchema,
  TemplateEvidenceRefusalSchema,
} from '../../lib/api/templates.gen';
import type {
  Template,
  TemplateAction,
  TemplateEvidenceRefusal,
  TemplateVersion,
} from '../../lib/api/templates.gen';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Callout, Cluster, DialogActions, Form, FormActions, FormError, FormField, Inline, Modal, ModalBody, ModalFooter, ModalHeader, Select, Stack, StatusChip, Textarea, ToolbarActions, useZodForm } from '../ui';
import type { StatusChipTone, StatusChipWeight } from '../ui';
import { PermissionGate } from '../access/PermissionGate';
import { useAuth } from '../../lib/auth';
import { errorBody, errorMessage, errorStatus } from '../../lib/errorMessage';

// The template editor's lifecycle affordance (P04 §4, §6.1–6.2; stories 4-1a,
// 4-1b).
//
// DOD §1.10 is why this lives in the same story as the endpoints: publish,
// archive, restore, submit, approve and reject are mutating endpoints exposed to
// a human principal, so each needs a control on a design-system-composed screen
// in the story that introduces it, not a later one.
//
// ── The two things this UI has to get right ───────────────────────────────
// 1. "Saved" and "live" are different facts, and before 4-1a the editor had one
//    word for both. `status` describes the working head; `publishedVersion`
//    describes what every inspector is capturing against right now (P04 §1:
//    "published stays live until re-publish"). The bar always states BOTH.
// 2. It does not decide what is legal. P04 §6.2 makes the transition table the
//    server's, and `template.actions[]` is that table projected onto this row —
//    so a control renders enabled iff the server says the move is legal, and its
//    tooltip is the server's own sentence when it is not. A bar that computed
//    "can I publish?" from `status` would be a second state machine, and the two
//    would disagree the first time an org switched review on.
//
//    `actions[]` answers STATE legality only. Whether YOU may press a legal
//    action is `PermissionGate`'s question, which is why both wrap every
//    control: an inspector must not be told a template cannot be published when
//    the truth is that they personally cannot publish it.
//
// ── Why it lives in components/, not modules/ ─────────────────────────────
// The same reason `components/inspections/TypePicker.tsx` does: its consumer
// (`pages/TemplateEditorPage.tsx`) is shell code, and the shell may not import a
// module — the pluggability test the `no-restricted-imports` rule enforces. The
// template LIBRARY (story 4-2a) is the surface that becomes a module.
//
// Everything composes design-system primitives (Button / StatusChip / Modal / Form)
// and reads through the generated client + its query-key factory (G-19, G-26) —
// no inline key arrays, no hand-rolled fetch, and the reject-note form takes its
// zod schema straight from `templates.gen.ts` rather than re-declaring it.
//
// KNOWN, ROUTED: `useQuery` on `listTemplateVersions` trips the G-18 warn
// selector (a `list*()` client function outside `useCursorList`). Left
// UNSUPPRESSED so Epic 2Y / story 2-72 finds it in the sweep. The endpoint is
// deliberately not a cursor list today: a history panel that paged would hide v1
// behind "load more", and restoring an early version is the main thing it is
// for.

export interface TemplatePublishBarProps {
  templateId: string;
  /**
   * True while the editor holds unsaved local edits. Acting then would freeze or
   * send the LAST SAVED draft, not what is on screen — a silently wrong result —
   * so the action is blocked and says why.
   */
  dirty?: boolean;
  /** Refetch the owning page's preset after a lifecycle change. */
  onChanged?: () => void;
  /** Optional document-studio notice host; actions stay here while guidance renders above the canvas. */
  noticeTargetTestId?: string;
}

function LifecycleNotice({
  targetTestId,
  children,
}: {
  targetTestId?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  const target = targetTestId && typeof document !== 'undefined'
    ? document.querySelector<HTMLElement>(`[data-testid="${targetTestId}"]`)
    : null;
  return target ? createPortal(children, target) : <>{children}</>;
}

// Story 2-146b: this used to be a hand-rolled `statusTone`/`statusLabel`
// pair standing in for `StatusChip` (2-141's own primitive) — exactly the
// "module-local status imitation" that story exists to end, and the SAME
// four-value vocabulary `TemplateCard.tsx`'s own `STATUS_META` already
// converted (see that file's comment). Not imported from there: this file is
// `components/templates/` (shell code, `TemplateEditorPage`'s consumer),
// `TemplateCard` is `modules/templates/` — importing across that boundary is
// exactly the cross-module import the shell's `no-restricted-imports` rule
// forbids, so the map is repeated here rather than shared.
//
// Exported (story 2-147wd, direct live user feedback: "status should go
// beside the title following our design system"): `TemplateEditorPage.tsx`
// (the same shell layer, its own consumer, not a module) now renders the
// record's status as a `WorkspaceHeader` `chips` entry, reusing these SAME
// three maps rather than a third near-duplicate copy — the identical
// "don't re-derive a rule a sibling file already owns" reasoning this
// comment's own first paragraph already applies to `TemplateCard`.
export const STATUS_TONE: Record<Template['status'], StatusChipTone> = {
  draft: 'neutral',
  in_review: 'warning',
  published: 'success',
  // Not `destructive` — archived is retired-but-fine, not an error. `bold`
  // weight (below) is what keeps it visually distinct from the subtle draft chip.
  archived: 'neutral',
};

export const STATUS_WEIGHT: Partial<Record<Template['status'], StatusChipWeight>> = {
  archived: 'bold',
};

export const STATUS_LABEL: Record<Template['status'], string> = {
  draft: 'Draft',
  in_review: 'In review',
  published: 'Published',
  archived: 'Archived',
};

/** The server's verdict for one action, or a safe "not legal" when absent. */
const verdict = (template: Template | undefined, action: TemplateAction['action']): TemplateAction =>
  template?.actions.find((a) => a.action === action) ?? { action, allowed: false, reason: 'Loading…' };

interface BlockedArchive {
  types: { typeId: string; typeName: string; requiredFields: string[] }[];
  replacements: { templateId: string; name: string; publishedVersion: number | null }[];
}

interface InvalidSubmit {
  issues: string[];
  types: { typeId: string; typeName: string; active: boolean; missingFields: string[] }[];
}

/**
 * The pre-5-10f capture-evidence refusal (P04 §7 ¶5; story 4-2e), carrying both
 * halves the server sent: the sentence a person reads, and the structured
 * offenders + remediation a person acts on.
 *
 * Parsed with the GENERATED contract schema rather than cast, because this is
 * the one refusal whose whole value is its structure — a cast that silently
 * accepted a body missing `fields` would render an empty list under a heading
 * promising "the exact unsupported fields", which is worse than the raw status
 * string it replaces.
 */
interface EvidenceBlock {
  message: string;
  refusal: TemplateEvidenceRefusal;
}

export function TemplatePublishBar({
  templateId,
  dirty = false,
  onChanged,
  noticeTargetTestId,
}: TemplatePublishBarProps): JSX.Element | null {
  const queryClient = useQueryClient();
  // Story 4-4: separation of duties is the one lifecycle rule whose answer
  // depends on WHO is looking, so this bar needs the viewer's identity. Read
  // from the auth context rather than fetched — it is already loaded for every
  // authenticated screen, and a second `/me` read to draw a tooltip would be a
  // request per editor open.
  const { user } = useAuth();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  // Story 2-146b: archive used to fire on ONE click with no confirmation step
  // — the "explains disabled ones" convention this bar otherwise follows
  // covers WHY a control is unavailable, not what a LEGAL, ENABLED destructive
  // one is about to do. `ShareLinksPanel.tsx`'s `RevokeConfirmDialog` is the
  // established shape for exactly this ("a confirm that states the
  // immediate, irreversible effect") — reused here rather than a new pattern.
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the server refuses on a stale revision (P04 §6.1). Held separately
  // from `error` because it is not a failure to apologise for — it is another
  // person's publish, and the honest response is to show what is live now.
  const [conflict, setConflict] = useState<{ currentVersion: number | null; currentRevision: number } | null>(null);
  // The submit-time fix list (P04 §6.2). Kept whole: the server reports every
  // problem at once so the author fixes them in one sitting.
  const [invalid, setInvalid] = useState<InvalidSubmit | null>(null);
  // The reassignment dialog P04 §4 asks for, driven entirely by the refusal.
  const [blocked, setBlocked] = useState<BlockedArchive | null>(null);
  const [replacementId, setReplacementId] = useState('');
  // The signature/GPS publication gate's refusal (story 4-2e). Its own slot,
  // not `error`, for the same reason `invalid` has one: it is a list of named
  // fields plus the swaps that would fix each of them, and collapsing that into
  // one string is exactly the loss this story was filed to stop.
  const [evidenceBlock, setEvidenceBlock] = useState<EvidenceBlock | null>(null);

  const lifecycleQuery = useQuery({
    queryKey: templatesKeys.detail(templateId),
    queryFn: () => getTemplateLifecycle(templateId),
    staleTime: 30_000,
  });
  const versionsQuery = useQuery({
    queryKey: [...templatesKeys.detail(templateId), 'versions'] as const,
    queryFn: () => listTemplateVersions(templateId),
    staleTime: 30_000,
    // Only the history panel needs it; the bar itself reads the lifecycle row.
    enabled: historyOpen,
  });

  const template = lifecycleQuery.data?.data;
  const versions: TemplateVersion[] = versionsQuery.data?.data ?? [];
  const liveVersion = template?.publishedVersion ?? null;
  const hasUnpublishedChanges = (template?.draftVersion ?? null) !== null;

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: templatesKeys.detail(templateId) });
    onChanged?.();
  };

  /**
   * Every lifecycle call routes through here so the four structured refusals are
   * handled in ONE place. None of them is an error to apologise for: each is the
   * server being right about something this tab could not have known, and each
   * carries what to do next.
   */
  const runLifecycle = async <T,>(call: () => Promise<T>): Promise<T | null> => {
    setError(null);
    setConflict(null);
    setInvalid(null);
    setEvidenceBlock(null);
    try {
      const result = await call();
      setBlocked(null);
      await invalidate();
      return result;
    } catch (err: unknown) {
      const body = errorBody(err) as Record<string, unknown> | undefined;
      // The pre-5-10f capture-evidence gate (P04 §7 ¶5, story 4-2e). Checked
      // through the generated schema: a 422 that says `unsupported_evidence`
      // but does not carry the fields is not renderable as this surface, and
      // falls through to the generic branch below with the server's sentence
      // intact rather than rendering an empty "here are the fields" panel.
      if (body?.reason === 'unsupported_evidence') {
        // `.passthrough()` at the TOP level only: `middlewares/error.js` merges
        // `err.expose` next to its own `code`/`message` (and `stack` in dev), so
        // the contract's `.strict()` would reject the very body it describes.
        // The nested objects stay strict — those come from `expose` verbatim.
        const parsed = TemplateEvidenceRefusalSchema.passthrough().safeParse(body);
        if (parsed.success) {
          setEvidenceBlock({ message: errorMessage(err), refusal: parsed.data });
          return null;
        }
      }
      if (body?.reason === 'revision_conflict') {
        setConflict({
          currentVersion: (body.currentVersion as number | null) ?? null,
          currentRevision: (body.currentRevision as number) ?? 0,
        });
        await invalidate();
        return null;
      }
      if (body?.reason === 'template_invalid') {
        setInvalid({
          issues: (body.issues as string[]) ?? [],
          types: (body.types as InvalidSubmit['types']) ?? [],
        });
        return null;
      }
      if (body?.reason === 'linked_active_types') {
        setBlocked({
          types: (body.types as BlockedArchive['types']) ?? [],
          replacements: (body.replacements as BlockedArchive['replacements']) ?? [],
        });
        return null;
      }
      if (body?.reason === 'separation_of_duties') {
        // Preparer ≠ approver (P13 D4, story 4-4). Deliberately handled ABOVE
        // the generic 403 branch below: that branch's copy is written for
        // "your access changed while you were editing", which is the wrong
        // story entirely — nothing about this person's access changed, and
        // nothing they do to their own role will make the call succeed. The
        // server's own sentence already names the remedy (hand it to a
        // colleague holding the capability), so G-27 applies unmodified: show
        // it, do not re-author it.
        //
        // No refetch. `illegal_transition` invalidates because the ROW moved;
        // here the row is exactly where this tab thought it was, and refetching
        // would only redraw the same enabled button. `approvalChain[]` on the
        // template is what stops this being a surprise in the first place.
        setError(errorMessage(err));
        return null;
      }
      if (body?.reason === 'illegal_transition') {
        // The row moved under this tab. Say what the server said — it is already
        // written for a person — and refetch so the bar matches reality.
        setError(String(body.detail ?? 'That is no longer possible on this template.'));
        await invalidate();
        return null;
      }
      // G-27, and the exact drift the gate was written for: this line used to
      // read `err.message`, which for every axios rejection is the TRANSPORT's
      // summary. A 422 `unsupported_evidence` — a body naming each offending
      // field, its section and the photo/text swap that would fix it — rendered
      // here as "Request failed with status code 422", and the whole refusal
      // was invisible unless someone opened the network tab. `errorMessage()`
      // is the shared unwrap point, with the one honest generic fallback for a
      // body that carries nothing.
      if (errorStatus(err) === 403 || errorStatus(err) === 401) {
        // "Permission revoked mid-edit" (P04 §7 ¶4): the server's own word here
        // is the bare "Forbidden", which tells the author nothing they can act
        // on. This is G-27's structured-reason carve-out — `PERMISSION_DENIED`
        // arrives with the permission key, so the sentence names it.
        const permission = typeof body?.permission === 'string' ? body.permission : null;
        setError(
          permission
            ? `Your access changed while you were editing — this now needs "${permission}", which your role no longer includes. Nothing you have written is lost; ask an owner or admin to restore it.`
            : `${errorMessage(err)} Nothing you have written is lost.`,
        );
        await invalidate();
        return null;
      }
      setError(errorMessage(err));
      return null;
    }
  };

  const publishMutation = useMutation({ mutationFn: () => runLifecycle(() => publishTemplate(templateId, {})) });
  const submitMutation = useMutation({ mutationFn: () => runLifecycle(() => submitTemplate(templateId, {})) });
  const approveMutation = useMutation({ mutationFn: () => runLifecycle(() => approveTemplate(templateId, {})) });
  const archiveMutation = useMutation({
    mutationFn: (replacementTemplateId?: string) =>
      runLifecycle(() => archiveTemplate(templateId, replacementTemplateId ? { replacementTemplateId } : {})),
  });
  const restoreMutation = useMutation({
    mutationFn: (version: number) => runLifecycle(() => restoreTemplateVersion(templateId, version, {})),
  });

  // G-26: the schema is the generated one, not a local re-declaration. `note` is
  // the only field the reviewer supplies — `expectedRevision` is the page's to
  // send, not theirs to type — so the contract schema is narrowed with `.pick()`
  // rather than replaced.
  const rejectForm = useZodForm(RejectTemplateBodySchema.pick({ note: true }), { defaultValues: { note: '' } });

  if (lifecycleQuery.isLoading) {
    return <span className="text-xs text-muted-foreground">Loading version…</span>;
  }
  if (lifecycleQuery.isError || !template) return null;

  const publishVerdict = verdict(template, 'publish');
  const submitVerdict = verdict(template, 'submit');
  const approveVerdict = verdict(template, 'approve');
  const rejectVerdict = verdict(template, 'reject');
  const archiveVerdict = verdict(template, 'archive');

  // Publishing/submitting the last saved draft while newer edits sit unsaved on
  // screen would act on the wrong thing. This is the ONE reason the client adds
  // on top of the server's table, because it is the one fact the server cannot
  // see: what is on this screen but not yet sent.
  const unsavedReason = dirty ? 'Save your changes first — this acts on what is saved, not on what is on screen.' : null;

  // P04 §4's context CTA: Draft→"Submit for review" (review on) or "Publish"
  // (review off); in_review→"Approve · Reject"; published→"New draft" is just
  // editing, which the editor already is.
  const primary = template.reviewRequired
    ? template.status === 'in_review'
      ? ('approve' as const)
      : ('submit' as const)
    : ('publish' as const);

  // Separation of duties (P13 D4, story 4-4). The SECOND reason this bar adds on
  // top of the server's table, and — like `unsavedReason` — it is here because
  // `actions[]` structurally cannot carry it: that table answers questions about
  // a ROW and is deliberately person-free, so "may YOU be the one" has to be
  // computed against the viewer.
  //
  // Read from `approvalChain[]`, never re-derived: the step's own
  // `requireDistinctFrom` is what the server will enforce in `freezeDraft`, so a
  // bar that compared `createdBy` itself would be a second copy of the rule that
  // drifts the day D25 adds a party. With `templates.sod` off every step's
  // `requireDistinctFrom` is empty and this is `null` — a true no-op.
  //
  // It DISABLES rather than hides: P04 §6.2's "explain disabled ones" is the
  // whole pattern of this bar, and a Publish button that silently vanished would
  // leave the author wondering whether they had lost a permission.
  const sodStep =
    template.approvalChain.find(
      (step) => step.requireDistinctFrom.includes('preparer') && step.preparerId !== null && step.preparerId === user?.id,
    ) ?? null;
  const sodReason = sodStep
    ? `You wrote this template, so someone else has to ${primary === 'approve' ? 'approve' : 'publish'} it. ` +
      `Ask a colleague with "${sodStep.permission}" to take a look.`
    : null;

  return (
    <>
      {/* Story 2-147wd, direct live user feedback: the status chip + "v{X}
          live"/"unpublished changes"/"Never published" text this row used
          to open with is GONE — `TemplateEditorPage.tsx` now renders the
          record's status as a `WorkspaceHeader` `chips` entry beside the
          title ("status should go beside the title... no redundancy no
          version info"), reusing this file's own exported `STATUS_TONE`/
          `STATUS_LABEL`/`STATUS_WEIGHT` maps rather than a third copy. The
          version NUMBER stays real and reachable — inside the History
          drawer's own version list (`historyOpen` below) — just not
          repeated a second time in this compact action row. `hasUnpublishedChanges`
          is unaffected (still read, only its VISIBLE text left); `liveVersion`
          is still used by the History list itself, so neither variable
          became dead.
          History is now an icon-only `IconButton` ("for history we can have
          an icon only button") rather than a labelled `Button` — the
          version-history affordance opens a drawer (`historyOpen` below,
          `placement="right"`), not a label-worthy inline action, matching
          this row's new leaner shape. Publish/Submit/Approve flip to
          `variant="primary"` and Save (`TemplateEditorPage.tsx`, this bar's
          sibling in the bottom action bar) flips to `variant="secondary"`
          — "Publish should be primary... save should be secondary": the
          lifecycle-advancing action is the more consequential one, Save is
          "keep my draft," Publish/Submit/Approve is "move it forward." */}
      <Cluster gap={3}>
        <ToolbarActions
          size="sm"
          actions={[
            {
              id: 'history',
              label: 'Version history',
              variant: 'ghost',
              iconOnly: true,
              icon: <ClockIcon className="h-4 w-4" />,
              onClick: () => setHistoryOpen(true),
              'data-testid': 'history-button',
            },
          ]}
        />

        {primary === 'submit' && (
          <PermissionGate need="templates:create">
            <ToolbarActions
              size="sm"
              actions={[
                {
                  id: 'submit',
                  label: 'Submit for review',
                  variant: 'primary',
                  loading: submitMutation.isPending,
                  disabled: !submitVerdict.allowed || unsavedReason !== null || submitMutation.isPending,
                  disabledReason: unsavedReason ?? submitVerdict.reason ?? 'Send this draft to a reviewer',
                  onClick: () => submitMutation.mutate(),
                },
              ]}
            />
          </PermissionGate>
        )}

        {primary === 'approve' && (
          <PermissionGate need="templates:publish">
            <ToolbarActions
              size="sm"
              actions={[
                {
                  id: 'approve',
                  label: 'Approve',
                  variant: 'primary',
                  loading: approveMutation.isPending,
                  disabled: !approveVerdict.allowed || sodReason !== null || approveMutation.isPending,
                  disabledReason: sodReason ?? approveVerdict.reason ?? 'Approve — this publishes the reviewed draft',
                  onClick: () => approveMutation.mutate(),
                  'data-testid': 'approve-button',
                },
                {
                  id: 'reject',
                  label: 'Reject',
                  variant: 'ghost',
                  disabled: !rejectVerdict.allowed,
                  disabledReason: rejectVerdict.reason ?? 'Send it back to the author with a note',
                  onClick: () => setRejectOpen(true),
                },
              ]}
            />
          </PermissionGate>
        )}

        {primary === 'publish' && (
          <PermissionGate need="templates:publish">
            <ToolbarActions
              size="sm"
              actions={[
                {
                  id: 'publish',
                  label: 'Publish',
                  variant: 'primary',
                  loading: publishMutation.isPending,
                  disabled:
                    !publishVerdict.allowed ||
                    unsavedReason !== null ||
                    sodReason !== null ||
                    !hasUnpublishedChanges ||
                    publishMutation.isPending,
                  disabledReason:
                    unsavedReason ??
                    sodReason ??
                    publishVerdict.reason ??
                    (hasUnpublishedChanges
                      ? 'Publish this template so new inspections use it'
                      : 'Nothing to publish: there are no unpublished changes.'),
                  onClick: () => publishMutation.mutate(),
                  'data-testid': 'publish-button',
                },
              ]}
            />
          </PermissionGate>
        )}
      </Cluster>

      {/* P13 D4's "explain disabled ones" half (story 4-4): the CTA above is
          disabled with this same sentence as its tooltip, and a tooltip alone is
          not reachable by keyboard or touch. The server's own refusal says the
          same thing if the call is made anyway — one rule, stated the same way
          before and after. */}
      {(sodReason || template.status === 'in_review' || template.reviewNote || conflict) && (
        <LifecycleNotice targetTestId={noticeTargetTestId}>
          <Stack gap={1}>
            {sodReason && (
              <p className="text-xs text-warning-subtle-text" data-testid="sod-reason">
                {sodReason}
              </p>
            )}
            {/* P04 §6.2's "explains disabled ones", for the state a reviewer created. */}
            {template.status === 'in_review' && (
              <p className="text-xs text-warning-subtle-text">
                Out for review since {template.submittedAt ? new Date(template.submittedAt).toLocaleDateString() : 'recently'} —
                the content is locked so the reviewer reads exactly what was sent.
              </p>
            )}
            {template.status !== 'in_review' && template.reviewNote && (
              <p className="text-xs text-warning-subtle-text">Sent back: {template.reviewNote}</p>
            )}

            {conflict && (
              <p className="text-xs text-warning-subtle-text">
                Someone else published while you were editing — v{conflict.currentVersion ?? '?'} is live now. Open History to
                compare before publishing again.
              </p>
            )}
          </Stack>
        </LifecycleNotice>
      )}
      {/* ── The pre-5-10f capture-evidence refusal (P04 §7 ¶5; story 4-2e) ──
          "Validation identifies the exact unsupported fields and offers
          photo/text alternatives." Both halves render, and both come off the
          wire: the server's sentence, then every offending field by section +
          label + type, then the remediation table for only the evidence kinds
          this template actually used. Nothing here is re-authored on the
          client — the backend owns this copy (`config/captureEvidence.js`) so
          that when 5-10f lifts the gate, deleting the entry is the whole
          change.

          A Modal, for the same reason `linked_active_types` below is one: this
          bar is a flex ITEM in the editor's wrapping toolbar, so anything
          rendered inline here inherits a ~110px column (measured at 1280px —
          the short one-line notices above get away with it, a two-field
          refusal with four remediations does not). The precedent in this file
          is that a refusal carrying a LIST goes in a Modal, which also gives
          the focus trap and Escape a panel this dense needs. */}
      {evidenceBlock && (
        <Modal onClose={() => setEvidenceBlock(null)} label="This template cannot be published yet">
          {/* No `title` on ModalHeader — `Callout` below already renders this
              exact heading visibly (`headingLevel={2}`); ModalHeader here
              only supplies the close affordance, not a second duplicate
              heading. */}
          <ModalHeader onClose={() => setEvidenceBlock(null)} />
          <ModalBody>
          <div className="space-y-3" data-testid="evidence-gate-refusal">
            <Callout tone="warning" headingLevel={2} title="This template cannot be published yet">
              <p>{evidenceBlock.message}</p>
            </Callout>

            <div className="mt-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">
                The fields that block it ({evidenceBlock.refusal.fields.length})
              </h3>
              <ul className="mt-1 space-y-0.5 text-sm" data-testid="evidence-gate-fields">
                {evidenceBlock.refusal.fields.map((field) => (
                  <li key={`${field.sectionName}:${field.fieldKey}`}>
                    <span className="font-medium text-foreground">{field.fieldLabel}</span>{' '}
                    <span className="text-muted-foreground">
                      — “{field.sectionName}” · <code>{field.fieldKey}</code> · {field.fieldType} ·{' '}
                      {field.requirement === 'always' ? 'always required' : 'required by a rule'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {evidenceBlock.refusal.evidence.map((kind) => (
              <div key={kind.kind}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">{kind.label}</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">{kind.detail}</p>
                <ul className="mt-1 space-y-0.5 text-sm" data-testid={`evidence-gate-alternatives-${kind.kind}`}>
                  {kind.alternatives.map((alternative) => (
                    <li key={alternative.type}>
                      <span className="font-medium text-foreground">Swap to {alternative.label}</span>{' '}
                      <span className="text-muted-foreground">— {alternative.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* The way forward, and the honest "when". Changing a field's type
                is the editor's own job (the response-type picker, directly
                behind this dialog), so this points at it rather than growing a
                second, half-wired swap control here — G-29. */}
            <p className="text-sm text-muted-foreground" data-testid="evidence-gate-remedy">
              Change those fields in the editor behind this dialog — make them optional, or switch the response type to
              a photo or a text answer — then publish again. Real signature and location capture arrives with story{' '}
              {evidenceBlock.refusal.unblockedByStory}; until it does, publishing this would give inspectors a question
              they cannot honestly answer.
            </p>
          </div>
          </ModalBody>
          <ModalFooter>
            <DialogActions
              actions={[
                {
                  id: 'back-to-editor',
                  label: 'Back to the editor',
                  variant: 'primary',
                  onClick: () => setEvidenceBlock(null),
                  'data-testid': 'evidence-gate-close',
                },
              ]}
            />
          </ModalFooter>
        </Modal>
      )}
      {(invalid || error) && (
        <LifecycleNotice targetTestId={noticeTargetTestId}>
          <Stack gap={1}>
            {invalid && (
              <Stack gap={1} className="text-xs text-destructive">
                <p className="font-medium">This draft cannot go out for review yet.</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  {invalid.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                  {invalid.types.map((type) => (
                    <li key={type.typeId}>
                      “{type.typeName}”{type.active ? '' : ' (inactive)'} needs {type.missingFields.join(', ')}
                    </li>
                  ))}
                </ul>
              </Stack>
            )}
            {error && <p className="text-xs font-medium text-destructive">{error}</p>}
          </Stack>
        </LifecycleNotice>
      )}

      {rejectOpen && (
        <Modal onClose={() => setRejectOpen(false)} label="Send this template back">
          {/* No ModalFooter: `FormSubmit` is owned by this `<Form>`'s own
              subtree (react-hook-form's `FormProvider` context does not
              extend past it), so both actions stay inside `ModalBody`. */}
          <ModalHeader title="Send this template back" onClose={() => setRejectOpen(false)}>
            <p className="text-xs text-muted-foreground">
              The author sees this note while they fix it. Nothing about their draft is changed.
            </p>
          </ModalHeader>
          <ModalBody>
          <Form
            form={rejectForm}
            className="space-y-3"
            onSubmit={async (values) => {
              const result = await runLifecycle(() => rejectTemplate(templateId, { note: values.note }));
              if (result) {
                setRejectOpen(false);
                rejectForm.reset({ note: '' });
              }
            }}
          >
            <FormField
              name="note"
              label="Why is it not ready?"
              required
              description="Be specific — this is the only record of why the work came back."
            >
              {(field) => <Textarea rows={4} {...field} />}
            </FormField>
            <FormError message={error} />
            <FormActions
              size="sm"
              actions={[
                { id: 'cancel', label: 'Cancel', variant: 'ghost', onClick: () => setRejectOpen(false) },
                { id: 'send-back', label: 'Send back', type: 'submit', variant: 'destructive', pendingLabel: 'Sending…' },
              ]}
            />
          </Form>
          </ModalBody>
        </Modal>
      )}

      {/* P04 §4's reassignment dialog, rendered from the refusal itself — the
          server already told us which types block and which templates could
          take over, so there is no second query and no chance of offering a
          replacement that would not work. */}
      {blocked && (
        <Modal onClose={() => setBlocked(null)} label="Choose a replacement template">
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">This template is still in use</h2>
            <p className="text-sm text-muted-foreground">
              {blocked.types.length === 1 ? 'An active inspection type has' : `${blocked.types.length} active inspection types have`}{' '}
              no other template to fall back on:
            </p>
            <ul className="list-disc space-y-0.5 pl-4 text-sm text-foreground">
              {blocked.types.map((type) => (
                <li key={type.typeId}>{type.typeName}</li>
              ))}
            </ul>
            {blocked.replacements.length > 0 ? (
              <>
                <label className="block text-sm font-medium text-foreground" htmlFor="replacement-template">
                  Move them to
                </label>
                <Select
                  id="replacement-template"
                  value={replacementId}
                  onChange={(e) => setReplacementId(e.target.value)}
                >
                  <option value="">Choose a template…</option>
                  {blocked.replacements.map((candidate) => (
                    <option key={candidate.templateId} value={candidate.templateId}>
                      {candidate.name}
                      {candidate.publishedVersion !== null ? ` (v${candidate.publishedVersion} live)` : ''}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only templates that are published and declare every field those types require are listed.
                </p>
              </>
            ) : (
              <p className="text-sm text-warning-subtle-text">
                No other published template declares the fields those types require. Deactivate the inspection types, or
                publish a replacement first.
              </p>
            )}
            <DialogActions
              className="border-t border-border/60 pt-3"
              actions={[
                { id: 'cancel', label: 'Cancel', variant: 'ghost', onClick: () => setBlocked(null) },
                {
                  id: 'move-and-archive',
                  label: 'Move and archive',
                  variant: 'destructive',
                  disabled: !replacementId || archiveMutation.isPending,
                  loading: archiveMutation.isPending,
                  onClick: () => archiveMutation.mutate(replacementId),
                },
              ]}
            />
          </div>
        </Modal>
      )}

      {historyOpen && (
        // Story 2-147wd, direct live user feedback on `TemplateEditorPage`
        // (this bar's own `lifecycle` consumer): `placement="right"` — a
        // real drawer, not a centered dialog. A version list plus its own
        // restore/archive actions reads better as a side panel someone
        // dismisses back into the editor than as a modal interrupting the
        // canvas dead-center. No other prop/behavior changed.
        <Modal onClose={() => setHistoryOpen(false)} label="Version history" placement="right">
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">Version history</h2>
            <p className="text-xs text-muted-foreground">
              Published versions are immutable. Inspections keep the version they were created under, so publishing a new
              one never changes work already underway.
            </p>
            <ul className="divide-y divide-border/60">
              {versions.map((version) => (
                <Inline key={version.version} as="li" gap={3} className="py-2">
                  <span className="w-12 shrink-0 text-sm font-semibold text-foreground">v{version.version}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-foreground">
                      {version.changeNote ?? (version.status === 'draft' ? 'Working draft' : 'No change note')}
                    </span>
                    {version.publishedAt && (
                      <span className="block text-xs text-muted-foreground">
                        Published {new Date(version.publishedAt).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                  {version.version === liveVersion && <StatusChip tone="success" label="Live" value="live" />}
                  {version.status === 'draft' && <StatusChip tone="warning" label="Draft" value="draft" />}
                  {version.status === 'published' && version.version !== liveVersion && (
                    <PermissionGate need="templates:create">
                      <ToolbarActions
                        size="sm"
                        actions={[
                          {
                            id: 'restore',
                            label: 'Restore as draft',
                            variant: 'ghost',
                            loading: restoreMutation.isPending,
                            disabled: !verdict(template, 'restore').allowed || restoreMutation.isPending,
                            // Restore replaces the working draft, so say so before it happens.
                            disabledReason:
                              verdict(template, 'restore').reason ??
                              'Copy this version into a new draft — it does not change what is live',
                            onClick: () => restoreMutation.mutate(version.version),
                          },
                        ]}
                      />
                    </PermissionGate>
                  )}
                </Inline>
              ))}
            </ul>
            <PermissionGate need="templates:publish">
              <div className="border-t border-border/60 pt-3">
                <ToolbarActions
                  size="sm"
                  actions={[
                    {
                      id: 'archive',
                      label: 'Archive template',
                      variant: 'destructive',
                      disabled: !archiveVerdict.allowed,
                      disabledReason: archiveVerdict.reason ?? 'Withdraw this template from the library',
                      onClick: () => setArchiveConfirmOpen(true),
                      'data-testid': 'archive-button',
                    },
                  ]}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Stops new inspections selecting it. Nothing already captured or reported is affected.
                </p>
              </div>
            </PermissionGate>
          </div>
        </Modal>
      )}

      {/* Story 2-146b: destructive consequences visible BEFORE confirmation —
          same shape as `ShareLinksPanel.tsx`'s `RevokeConfirmDialog`. Archive
          still routes through `archiveMutation`, so a `linked_active_types`
          refusal still opens the reassignment dialog exactly as before; this
          only adds the one click this action was missing. */}
      {archiveConfirmOpen && (
        <Modal onClose={() => setArchiveConfirmOpen(false)} label="Archive this template?" disableOutsideDismiss>
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">Archive this template?</h2>
            <p className="text-sm text-muted-foreground">
              Stops new inspections selecting it. Nothing already captured or reported is affected. Any inspection type
              that still needs it will ask you to choose a replacement first.
            </p>
            <DialogActions
              className="border-t border-border/60 pt-3"
              actions={[
                { id: 'cancel', label: 'Cancel', variant: 'ghost', onClick: () => setArchiveConfirmOpen(false) },
                {
                  id: 'confirm-archive',
                  label: 'Archive template',
                  variant: 'destructive',
                  loading: archiveMutation.isPending,
                  disabled: archiveMutation.isPending,
                  'data-testid': 'archive-confirm',
                  onClick: () => {
                    setArchiveConfirmOpen(false);
                    archiveMutation.mutate(undefined);
                  },
                },
              ]}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
